import { useEffect, useMemo, useRef, useState } from "react";
import { fmtMonth, idx, monthAt, N, sortMonths } from "@/lib/months";
import type { MetricNode } from "@/model/types";
import { fmtShort, fmtValue } from "@/lib/format";
import { useStore, useTerm } from "@/model/store";
import { cn } from "@/lib/utils";
import { linePath, niceTicks, PAD, useChartSurface, useWidth, yearTicks } from "./chart-utils";
import { ChartLegend, type LegendItem } from "./ChartToolbar";

type Range = Record<string, [number, number, number]>;
type Q = [ArrayLike<number>, ArrayLike<number>, ArrayLike<number>, ArrayLike<number>, ArrayLike<number>];

interface Props {
	node: MetricNode;
	range: Range;
	/** The stated bands through `range`: the API's exact series, or a straight-line preview while dragging. Null for formulas. */
	plan: { lo: ArrayLike<number>; mid: ArrayLike<number>; hi: ArrayLike<number> } | null;
	onChange?: (r: Range) => void;
	onCommit?: (r: Range) => void;
	implied?: Q | null;
	scenario?: Q | null;
	scenarioLabel?: string;
	/** Colour of the stated range: the baseline, a scenario's own values, or grey for values that are fixed here. */
	tone?: "baseline" | "scenario" | "muted";
	/**
	 * What the chart puts forward. "values" (default): the stated range, drawn strong. "result": the result with
	 * risks and its range, with the stated range drawn light behind it (a scenario's view).
	 */
	focus?: "values" | "result";
	/** The name of the result with risks, in the legend and tooltip. Defaults to "With risks". */
	resultLabel?: string;
	statedLabel?: string;
	/** A grey reference line (e.g. Baseline's likely value while editing a scenario). */
	ghost?: ArrayLike<number> | null;
	ghostLabel?: string;
	readOnly?: boolean;
	height?: number;
	viewEnd: number;
	/** First month shown (grid index; −1 is now, negatives are history). Defaults to 12 months of history. */
	viewStart?: number;
	labels: { low: string; likely: string; high: string };
	/** Draw the legend under the chart, built from the series shown ("History" only when there is some). */
	legend?: boolean;
}

/** Hit area for a handle: at least 44 px (J1), however small it looks. */
const HIT = 22;

type Drag = { month: string; which: 0 | 1 | 2; start: Range; removing: boolean; domain: [number, number] };

export function RangeEditor({
	node,
	range,
	plan,
	onChange,
	onCommit,
	implied,
	scenario,
	scenarioLabel = "Scenario",
	tone = "baseline",
	focus = "values",
	resultLabel,
	statedLabel = "Baseline",
	ghost,
	ghostLabel = "Baseline",
	readOnly: readOnlyProp,
	height = 300,
	viewEnd,
	viewStart = -12,
	labels,
	legend = false,
}: Props) {
	const surf = useChartSurface();
	const analyst = useStore((s) => s.analyst);
	const term = useTerm();
	// The node-page preview decides whether the chart is live (D1).
	const readOnly = surf.surface === "preview" ? !!surf.readOnly || !!readOnlyProp : !!readOnlyProp;
	const [wrapRef, W] = useWidth<HTMLDivElement>();
	const svgRef = useRef<SVGSVGElement>(null);
	const C = tone === "scenario" ? "var(--scenario)" : tone === "muted" ? "var(--muted-foreground)" : "var(--baseline)";
	const onResult = focus === "result";
	// Grey values are fixed here: no handles, only the line.
	const handles = tone !== "muted";
	const [drag, setDrag] = useState<Drag | null>(null);
	const [hover, setHover] = useState<{ t: number; px: number; py: number } | null>(null);
	const hoverT = hover?.t ?? null;
	const [focusKey, setFocusKey] = useState<string | null>(null);

	const growth = node.scale === "growth";
	const t0 = Math.min(-1, viewStart);
	const history = node.observed.filter((o) => idx(o.month) >= t0 && idx(o.month) <= viewEnd);
	const t1 = viewEnd;

	const computedDomain = useMemo((): [number, number] => {
		const vals: number[] = history.map((o) => o.value);
		const add = (arr?: ArrayLike<number> | null) => {
			if (!arr) return;
			for (let t = 0; t <= Math.min(t1, N - 1); t += 3) vals.push(arr[t]);
		};
		if (plan) {
			add(plan.lo);
			add(plan.hi);
		}
		add(implied?.[0]);
		add(implied?.[4]);
		add(scenario?.[0]);
		add(scenario?.[4]);
		add(ghost);
		for (const r of Object.values(range)) vals.push(...r);
		const finite = vals.filter(Number.isFinite);
		let lo = Math.min(...finite);
		let hi = Math.max(...finite);
		if (!finite.length) [lo, hi] = [0, 1];
		const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.2 || 1;
		lo = lo - pad;
		hi = hi + pad;
		if (growth || finite.every((v) => v >= 0)) lo = Math.max(0, lo);
		// The axis always includes zero, so bars of size read honestly.
		return [Math.min(0, lo), Math.max(0, hi)];
	}, [history, plan, implied, scenario, ghost, range, t1, growth]);
	const domain = drag?.domain ?? computedDomain;

	// Phones: a shorter preview (A2).
	const H = surf.surface === "preview" && W < 640 ? Math.min(height, 240) : height;
	const iw = W - PAD.l - PAD.r;
	const ih = H - PAD.t - PAD.b;
	const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0)) * iw;
	const y = (v: number) => PAD.t + (1 - (v - domain[0]) / (domain[1] - domain[0])) * ih;
	const tAt = (px: number) => Math.round(t0 + ((px - PAD.l) / iw) * (t1 - t0));
	const vAt = (py: number) => domain[0] + (1 - (py - PAD.t) / ih) * (domain[1] - domain[0]);

	const series = (arr: ArrayLike<number>, from = 0) => {
		const pts: [number, number][] = [];
		for (let t = from; t <= Math.min(t1, N - 1); t++) pts.push([x(t), y(arr[t])]);
		return pts;
	};
	const band = (lo: ArrayLike<number>, hi: ArrayLike<number>) => {
		const a = series(hi);
		const b = series(lo).reverse();
		return linePath([...a, ...b]) + "Z";
	};
	const lastObs = history.filter((o) => idx(o.month) < 0).slice(-1)[0];
	const startPt: [number, number] | null = lastObs ? [x(idx(lastObs.month)), y(lastObs.value)] : null;
	const withStart = (pts: [number, number][]) => (startPt ? [startPt, ...pts] : pts);

	const months = sortMonths(Object.keys(range)).filter((m) => idx(m) >= 0 && idx(m) <= t1);

	const local = (e: React.PointerEvent) => {
		const r = svgRef.current!.getBoundingClientRect();
		return { px: e.clientX - r.left, py: e.clientY - r.top };
	};

	function onMove(e: React.PointerEvent) {
		const { px, py } = local(e);
		if (!drag) {
			const t = tAt(px);
			setHover(px >= PAD.l && px <= W - PAD.r && t >= t0 && t <= t1 ? { t, px, py } : null);
			return;
		}
		const removing = drag.which === 1 && (py < PAD.t - 34 || py > H - PAD.b + 30) && Object.keys(drag.start).length > 1;
		const next: Range = { ...drag.start };
		let v = vAt(py);
		if (growth) v = Math.max(v, 1e-9);
		const [lo, mid, hi] = drag.start[drag.month];
		let month = drag.month;
		if (drag.which === 1) {
			// Moving "likely" carries the band with it.
			const ms = sortMonths(Object.keys(drag.start));
			const i = ms.indexOf(drag.month);
			const tmin = i > 0 ? idx(ms[i - 1]) + 1 : 0;
			const tmax = i < ms.length - 1 ? idx(ms[i + 1]) - 1 : N - 1;
			month = monthAt(Math.max(tmin, Math.min(tmax, tAt(px))));
			delete next[drag.month];
			if (removing) {
				onChange?.(next);
				if (!drag.removing) setDrag({ ...drag, removing });
				return;
			}
			next[month] = growth && mid > 0 ? [lo * (v / mid), v, hi * (v / mid)] : [lo + (v - mid), v, hi + (v - mid)];
		} else if (drag.which === 0) {
			next[month] = [Math.min(v, mid), mid, hi];
		} else {
			next[month] = [lo, mid, Math.max(v, mid)];
		}
		if (month !== drag.month)
			setDrag({
				...drag,
				month,
				start: { ...Object.fromEntries(Object.entries(drag.start).filter(([k]) => k !== drag.month)), [month]: drag.start[drag.month] },
				removing,
			});
		else if (removing !== drag.removing) setDrag({ ...drag, removing });
		onChange?.(next);
	}

	function onUp() {
		if (!drag) return;
		onCommit?.(roundRange(range));
		setDrag(null);
	}

	function onBackgroundDown(e: React.PointerEvent) {
		if (readOnly || !plan) return;
		// In the preview a tap on a phone is for looking; add dates there with a mouse or in the editor.
		if (surf.surface === "preview" && e.pointerType === "touch") return;
		const { px } = local(e);
		const t = tAt(px);
		if (t < 0 || t > t1) return;
		const m = monthAt(t);
		if (range[m]) return;
		onCommit?.(roundRange({ ...range, [m]: [plan.lo[t], plan.mid[t], plan.hi[t]] }));
	}

	const ticks = yearTicks(t0, t1, iw);
	const yt = niceTicks(domain[0], domain[1], 5);
	const hv = hoverT;
	const obsAt = hv !== null ? node.observed.find((o) => idx(o.month) === hv) : undefined;

	// Keyboard: the handles are sliders (up/down; Delete removes a date).
	function onKey(e: React.KeyboardEvent, m: string, k: 0 | 1 | 2) {
		if (readOnly) return;
		const cur = range[m];
		if (!cur) return;
		const [lo, mid, hi] = cur;
		const step = (((yt[1] ?? 1) - (yt[0] ?? 0)) / 10) * (e.shiftKey ? 5 : 1);
		const dir = e.key === "ArrowUp" ? 1 : e.key === "ArrowDown" ? -1 : 0;
		const next: Range = { ...range };
		if (dir) {
			if (k === 1) {
				const v = growth ? Math.max(1e-9, mid + dir * step) : mid + dir * step;
				next[m] = growth && mid > 0 ? [lo * (v / mid), v, hi * (v / mid)] : [lo + (v - mid), v, hi + (v - mid)];
			} else if (k === 0) next[m] = [Math.min(lo + dir * step, mid), mid, hi];
			else next[m] = [lo, mid, Math.max(hi + dir * step, mid)];
		} else if ((e.key === "Delete" || e.key === "Backspace") && k === 1 && Object.keys(range).length > 1) {
			delete next[m];
			setFocusKey(null);
		} else return;
		e.preventDefault();
		onCommit?.(roundRange(next));
	}

	useEffect(() => {
		if (!focusKey || !svgRef.current) return;
		const el = svgRef.current.querySelector<SVGElement>(`[data-key="${focusKey}"]`);
		if (el && document.activeElement !== el) el.focus();
	}, [focusKey, range]);

	// Ghost "+" at the pointer's month in the future (D6): clicking adds a date there.
	const nearHandle = hover !== null && months.some((m) => Math.abs(x(idx(m)) - hover.px) < HIT);
	const ghostAdd = !readOnly && plan && hv !== null && hv >= 0 && !drag && !range[monthAt(hv)] && !nearHandle ? { t: hv, v: plan.mid[hv] } : null;

	// Tooltip: follows the pointer vertically (D5); outside analyst mode, the two most relevant lines.
	const tipLines: { k: string; el: React.ReactNode }[] = [];
	if (hv !== null) {
		if (obsAt) tipLines.push({ k: "o", el: <>Observed {fmtValue(obsAt.value, node.unit)}</> });
		if (hv >= 0 && plan)
			tipLines.push({
				k: "p",
				el: (
					<span style={{ color: C }}>
						{statedLabel}: {fmtShort(plan.lo[hv], node.unit)} – {fmtShort(plan.mid[hv], node.unit)} – {fmtShort(plan.hi[hv], node.unit)}
					</span>
				),
			});
		if (hv >= 0 && implied)
			tipLines.push({
				k: "i",
				el: (
					<span className="text-[var(--model)]">
						{plan ? (resultLabel ?? "With risks") : "Calculated"}: {fmtShort(implied[0][hv], node.unit)} – {fmtShort(implied[2][hv], node.unit)} –{" "}
						{fmtShort(implied[4][hv], node.unit)}
					</span>
				),
			});
		if (hv >= 0 && scenario)
			tipLines.push({
				k: "s",
				el: (
					<span className="text-[var(--scenario-ink)]">
						{scenarioLabel}: {fmtShort(scenario[2][hv], node.unit)}
					</span>
				),
			});
		if (hv >= 0 && ghost)
			tipLines.push({
				k: "g",
				el: (
					<span className="text-muted-foreground">
						{ghostLabel}: {fmtShort(ghost[hv], node.unit)}
					</span>
				),
			});
	}
	const shownTip = analyst ? tipLines : tipLines.slice(0, 2);
	const tipTop = Math.max(0, Math.min(H - 30 - (shownTip.length + 1) * 18, (hover?.py ?? 0) - 24));

	const legendItems: LegendItem[] = [
		...(history.length > 0 ? [{ c: "var(--ink)", l: "History" }] : []),
		...(implied && onResult ? [{ c: "var(--model)", l: plan ? (resultLabel ?? term("With risks", "implied")) : "Calculated", dash: true }] : []),
		...(plan ? [{ c: C, l: tone === "baseline" ? term(statedLabel, "stated p10–p90") : `${statedLabel}: values` }] : []),
		...(implied && !onResult ? [{ c: "var(--model)", l: plan ? term("With risks", "implied") : "Calculated", dash: true }] : []),
		...(scenario ? [{ c: "var(--scenario)", l: scenarioLabel }] : []),
		...(ghost ? [{ c: "var(--muted-foreground)", l: ghostLabel }] : []),
	];

	return (
		<div ref={wrapRef} className="relative w-full select-none">
			<svg
				ref={svgRef}
				width={W}
				height={H}
				className={cn("block overflow-visible", drag && "cursor-grabbing")}
				onPointerMove={onMove}
				onPointerUp={onUp}
				onPointerCancel={onUp}
				onPointerLeave={() => setHover(null)}
				role="group"
				aria-label={`${node.title} chart`}
			>
				<rect
					x={x(0)}
					y={PAD.t}
					width={Math.max(0, x(t1) - x(0))}
					height={ih}
					fill="transparent"
					className={readOnly || !plan ? "" : "cursor-copy"}
					onPointerDown={onBackgroundDown}
				>
					{!readOnly && plan && <title>Click to add a date here</title>}
				</rect>
				{yt.map((v) => (
					<g key={v} pointerEvents="none">
						<line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--grid)" />
						<text x={PAD.l - 8} y={y(v) + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
							{fmtShort(v, node.unit)}
						</text>
					</g>
				))}
				{ticks.map((tk) => (
					<g key={tk.t} pointerEvents="none">
						<line x1={x(tk.t)} x2={x(tk.t)} y1={PAD.t} y2={H - PAD.b} stroke="var(--grid)" strokeDasharray="2 3" />
						<text x={x(tk.t)} y={H - PAD.b + 18} textAnchor="middle" className="fill-muted-foreground text-[11px]">
							{tk.label}
						</text>
					</g>
				))}
				<line x1={x(-0.5)} x2={x(-0.5)} y1={PAD.t} y2={H - PAD.b} stroke="var(--ink)" strokeOpacity={0.35} pointerEvents="none" />

				<g pointerEvents="none">
					{plan && (
						<>
							<path
								d={linePath([...withStart(series(plan.hi)), ...series(plan.lo).reverse(), ...(startPt ? [startPt] : [])]) + "Z"}
								fill={C}
								fillOpacity={onResult ? 0.06 : 0.1}
							/>
							<path d={linePath(withStart(series(plan.lo)))} fill="none" stroke={C} strokeOpacity={onResult ? 0.2 : 0.35} />
							<path d={linePath(withStart(series(plan.hi)))} fill="none" stroke={C} strokeOpacity={onResult ? 0.2 : 0.35} />
						</>
					)}
					{/* With the result in front, the stated line sits behind it. */}
					{plan && onResult && (
						<path d={linePath(withStart(series(plan.mid)))} fill="none" stroke={C} strokeOpacity={tone === "muted" ? 0.5 : 0.8} strokeWidth={2} />
					)}
					{ghost && <path d={linePath(withStart(series(ghost)))} fill="none" stroke="var(--muted-foreground)" strokeOpacity={0.6} strokeWidth={2} />}
					{implied && (
						<>
							<path
								d={band(implied[0], implied[4])}
								fill={onResult ? "var(--model)" : "none"}
								fillOpacity={onResult ? 0.14 : undefined}
								stroke="var(--model)"
								strokeOpacity={onResult ? 0.7 : 0.55}
								strokeDasharray="3 3"
							/>
							<path d={linePath(withStart(series(implied[2])))} fill="none" stroke="var(--model)" strokeWidth={onResult ? 3 : 2} strokeDasharray="6 4" />
						</>
					)}
					{scenario && (
						<>
							<path d={band(scenario[0], scenario[4])} fill="var(--scenario)" fillOpacity={0.1} />
							<path d={linePath(withStart(series(scenario[2])))} fill="none" stroke="var(--scenario)" strokeWidth={2.5} />
						</>
					)}
					{plan && !onResult && <path d={linePath(withStart(series(plan.mid)))} fill="none" stroke={C} strokeWidth={3} />}
					{history.length > 0 && <path d={linePath(history.map((o) => [x(idx(o.month)), y(o.value)]))} fill="none" stroke="var(--ink)" strokeWidth={2.5} />}
					{history.map((o) => (
						<circle key={o.month} cx={x(idx(o.month))} cy={y(o.value)} r={idx(o.month) >= 0 ? 4.5 : 2.5} fill="var(--ink)" />
					))}
				</g>

				{plan &&
					handles &&
					months.map((m) =>
						([0, 2, 1] as const).map((k) => {
							const t = idx(m);
							const v = range[m][k];
							const active = drag && drag.month === m && drag.which === k;
							const removing = active && drag!.removing;
							return (
								<g key={`${m}-${k}`} transform={`translate(${x(t)},${y(v)})`}>
									{active && !removing && (
										<g pointerEvents="none">
											<rect x={-66} y={-38} width={132} height={24} rx={5} fill="var(--ink)" />
											<text y={-22} textAnchor="middle" className="fill-white text-xs font-medium">
												{[labels.low, labels.likely, labels.high][k]}: {fmtShort(v, node.unit)}
											</text>
										</g>
									)}
									{!readOnly && (
										<circle
											r={HIT}
											fill="transparent"
											className={drag ? undefined : "cursor-grab"}
											style={{ touchAction: "none" }}
											onPointerDown={(e) => {
												e.stopPropagation();
												svgRef.current?.setPointerCapture(e.pointerId);
												setDrag({ month: m, which: k, start: range, removing: false, domain: computedDomain });
											}}
										>
											<title>{`${[labels.low, labels.likely, labels.high][k]} by ${fmtMonth(m)}: ${fmtValue(v, node.unit)}`}</title>
										</circle>
									)}
									{/* Focus ring (D4). */}
									{focusKey === `${m}-${k}` && !drag && <circle r={k === 1 ? 13 : 10} fill="none" stroke={C} strokeWidth={2} pointerEvents="none" />}
									<circle
										r={k === 1 ? (active ? 9 : 7) : 5}
										fill={removing ? "var(--risk)" : k === 1 ? "#fff" : C}
										stroke={removing ? "var(--risk)" : C}
										strokeWidth={k === 1 ? 3 : 1.5}
										className="pointer-events-none outline-none"
										tabIndex={readOnly ? -1 : 0}
										role="slider"
										data-key={`${m}-${k}`}
										aria-label={`${[labels.low, labels.likely, labels.high][k]} by ${fmtMonth(m)}`}
										aria-valuenow={v}
										aria-valuetext={fmtValue(v, node.unit)}
										onFocus={() => setFocusKey(`${m}-${k}`)}
										onBlur={() => setFocusKey((f) => (f === `${m}-${k}` ? null : f))}
										onKeyDown={(e) => onKey(e, m, k)}
									/>
								</g>
							);
						}),
					)}
				{drag?.removing && (
					<text x={W / 2} y={PAD.t + 14} textAnchor="middle" className="fill-[var(--risk)] text-xs font-medium">
						Release to remove this date
					</text>
				)}
				{hv !== null && !drag && <line x1={x(hv)} x2={x(hv)} y1={PAD.t} y2={H - PAD.b} stroke="var(--ink)" strokeOpacity={0.25} pointerEvents="none" />}
				{ghostAdd && (
					<g transform={`translate(${x(ghostAdd.t)},${y(ghostAdd.v)})`} pointerEvents="none" opacity={0.7}>
						<circle r={8} fill="var(--card)" stroke={C} strokeWidth={1.5} strokeDasharray="3 2" />
						<path d="M-4,0H4M0,-4V4" stroke={C} strokeWidth={1.5} />
					</g>
				)}
			</svg>
			{hv !== null && !drag && (
				<div
					className={cn("pointer-events-none absolute z-10 rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-sm")}
					style={{ left: x(hv) + 200 > W ? Math.max(0, x(hv) - 200) : x(hv) + 12, top: tipTop }}
				>
					<div className="font-medium">
						{fmtMonth(monthAt(hv))}
						{ghostAdd && <span className="font-normal text-muted-foreground"> · click to add a date</span>}
					</div>
					{shownTip.map((l) => (
						<div key={l.k}>{l.el}</div>
					))}
				</div>
			)}
			{(legend || surf.readout) && (
				<div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
					{legend && <ChartLegend items={legendItems} />}
					{surf.readout && <div className="flex w-full min-w-0 text-sm">{surf.readout}</div>}
				</div>
			)}
		</div>
	);
}

function roundRange(r: Range): Range {
	const sig = (v: number) => (v === 0 ? 0 : Number(v.toPrecision(3)));
	return Object.fromEntries(Object.entries(r).map(([m, [a, b, c]]) => [m, [sig(a), sig(b), sig(c)]])) as Range;
}
